import { RootState } from '@Common/models/redux/store';
// import { DocumentContext } from 'next/document';
import {
  Grid,
  Paper,
  Container,
  Typography,
  IconButton,
  Tooltip,
  Theme,
  styled,
  useMediaQuery,
} from '@quillbot/design-system';
import { makeStyles } from '@mui/styles';
import { CommentOutlined } from '@mui/icons-material';
import React, { useRef } from 'react';
import Head from 'next/head';
import { useDispatch, useSelector } from 'react-redux';
import { CitationGeneratorProvider } from 'src/citation-generator/context';
import withQuillLayout from 'src/_common/hoc/QuillLayout';
import FeedbackDrawer from 'src/_common/components/drawer/FeedbackDrawer';
import { toggleDrawerAction } from '@Common/actions/commonActions';
import { openDrawer } from '@Common/handlers/services';
import ToolBarAndResultContainer from 'src/citation-generator/components/TopBar';
import CitationFaqSection from 'src/citation-generator/components/Faq';
import { getCgBreakpointDown, getCgBreakpointUp } from 'src/citation-generator/utils';
import CitationListSection from 'src/citation-generator/components/CitationListSection';
import Info from 'src/citation-generator/components/Info';
import Features from 'src/citation-generator/components/Features';
import HowCGWorks from 'src/citation-generator/components/HowCGWorks';
import FormatsAndExample from 'src/citation-generator/components/FormatsAndExample';
import Basics from 'src/citation-generator/components/Basics';
import EditForm from 'src/citation-generator/components/EditForm';
import { EditFormProvider } from 'src/citation-generator/components/EditForm/context';

const useStyles = makeStyles((theme: Theme) => ({
  bgColorbody: {
    backgroundColor: theme.palette.background.default,
    paddingLeft: '42px',
    paddingRight: '42px',
    [getCgBreakpointDown(theme)]: {
      paddingLeft: 0,
      paddingRight: 0,
      paddingTop: '0px !important',
    },
  },
  rightBorder: {
    position: 'relative',
    [getCgBreakpointUp(theme)]: {
      borderRight: `1px solid ${theme.palette.divider}`,
      minHeight: 'calc(50vh + 125px)',
    },
  },
  pane: {
    padding: '0px 4px 0px 16px',
  },
  topHeading: {
    fontWeight: 'bold',
    // fontsize: '14px',
    marginBottom: '10px',
    [getCgBreakpointDown(theme)]: {
      fontSize: '12px',
      lineHeight: 1.3,
    },
  },
  topBar: {
    height: '48px',
    [getCgBreakpointDown(theme)]: {
      height: '30px',
    },
  },
  hideOnSmallScreens: {
    [getCgBreakpointDown(theme)]: {
      display: 'none !important',
    },
  },
  feedbackBtnWrapper: {
    position: 'absolute',
    top: 0,
    right: -40,
    height: 48,
    display: 'flex',
    alignItems: 'center',
  },
  midContainerResponsive: {
    boxSizing: 'border-box',
    [getCgBreakpointDown(theme)]: {
      minHeight: 'calc(100vh - 102px)',
      maxHeight: 'calc(100vh - 102px)',
    },
  },
}));

const StyledRootContainer = styled(Container)<{ independentTool: boolean }>`
  && {
    padding-top: 25px;
    padding-bottom: 30px;
    flex-direction: column;
    flex-wrap: unset !important;
    display: flex;
    padding: ${({ independentTool }: { independentTool: boolean }) =>
      independentTool ? '0px !important' : ''};
  }
`;

const useCitationResultHeaderStyle = makeStyles((theme) => ({
  topHeading: {
    fontWeight: 'bold',
    // fontsize: '14px',
    // marginBottom: '10px',
  },
}));

const useFeedBackButtonStyle = makeStyles((theme) => ({
  iconButton: {
    padding: '6px',
    '&:hover': {
      backgroundColor: 'inherit',
      // color: theme.palette.primary.main,
    },
  },
  iconColor: {
    color: theme.palette.primary.main,
  },
}));

const FeedbackButton = () => {
  const isFeedbackDrawerOpen = useSelector((state: RootState) => state.common.quillDrawer);
  const classes = useFeedBackButtonStyle();
  const dispatch = useDispatch();
  return (
    <Tooltip arrow title="Give Feedback">
      <IconButton
        onClick={() => {
          if (isFeedbackDrawerOpen) {
            dispatch(toggleDrawerAction(null));
          } else {
            openDrawer('FEEDBACK', FeedbackDrawer);
          }
        }}
        classes={{ root: classes.iconButton }}
        size="large"
      >
        <CommentOutlined
          fontSize="small"
          className={isFeedbackDrawerOpen ? classes.iconColor : ''}
        />
      </IconButton>
    </Tooltip>
  );
};

const CitationResultHeader = () => {
  const classes = useCitationResultHeaderStyle();
  return (
    <Grid container justifyContent="space-between" alignItems="center">
      <Typography component="h2" className={classes.topHeading}>
        Citation Generator
      </Typography>
    </Grid>
  );
};

/*

const useMobileStyles = makeStyles((theme) => ({
  container: {
    textAlign: 'center',
    backgroundColor: '#F4F5F5',
    padding: '0px 20px',
  },
  lostImg: {
    maxWidth: '650px',
    margin: 'auto',
    width: '100%',
  },
  smallTitle: {
    fontWeight: 400,
    fontSize: '23px',
    lineHeight: 'normal',
    marginBottom: '40px',
    marginTop: '40px',
    [theme.breakpoints.down(959)]: {
      fontSize: '23px',
    },
  },
  bigTitle: {
    fontWeight: 400,
    fontSize: '23px',
    lineHeight: '35px',
    margin: '0px 0px 40px',
    paddingTop: '50px',
    [theme.breakpoints.down(959)]: {
      fontSize: '23px',
    },
  },
  quillBtn: {
    color: '#fff',
    fontSize: '0.875rem',
    lineHeight: '51px',
    borderRadius: '100px',
    fontWeight: 700,
    padding: '6px 16px',
    marginBottom: '60px',

    [theme.breakpoints.down(959)]: {
      fontSize: '0.875rem',
      lineHeight: '1.75',
    },
  },
}));

const InfoForMobileUser = () => {
  const classes = useMobileStyles();
  const router = useRouter();
  const products = [
    {
      name: 'Paraphraser',
      link: '/',
    },
    {
      name: 'Grammar Checker',
      link: '/grammar-check',
    },
    {
      name: 'Summarizer',
      link: '/summarize',
    },
  ];
  const chooseProducts = products.filter((p) => !p.link.includes(router.pathname));
  const product = chooseProducts[Math.floor(Math.random() * chooseProducts.length)];
  return (
    <div className={classes.container}>
      <p className={classes.bigTitle}>
        Support for your device is
        <br /> still in the works.
      </p>
      <img alt="ErrorImage" src="/images/404Page.svg" className={classes.lostImg} />
      // </div>
      <p
        className={classes.smallTitle}
        // style={{ color: '#252525' }}
      >
        Meanwhile, try our
      </p>
      <div>
        <Link href={product.link}>
          <Button variant="contained" color="primary" className={classes.quillBtn}>
            {product.name}
          </Button>
        </Link>
      </div>
    </div>
  );
};
*/

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Is QuillBot citation generator free?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes! Our online citation generator is free to use. In fact, we love freebies, so if you haven’t checked out our other free writing and research tools yet, you’re missing out.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I export my list of full citations?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'You have the option of copying your entire list of full citations to your clipboard or exporting them to Word.',
      },
    },
    {
      '@type': 'Question',
      name: 'Do I have to create an account?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Accounts are cool and all, but sometimes you need to work and don’t have time to make up a new password. You can use the QuillBot platform tools, including the citation generator, for free without even signing in. You may eventually want an account for the perks of other features within the platform, but you can decide that for yourself whenever you’re ready.',
      },
    },
    {
      '@type': 'Question',
      name: 'What styles does the QuillBot citation generator support?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'The citation generator supports APA, MLA, and Chicago style formats for citing books and websites, but we are always working to add more variety to our tools. Which ones do you want us to add first? Let us know here in our feedback portal.',
      },
    },
    {
      '@type': 'Question',
      name: 'Are citations and references the same?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'No. A citation can be either full or in-text and notates a source that the writer has used to support their argument or research. There are many styles of citations, but the most commonly used styles are APA, MLA, and Chicago citation styles. A citation says, “Hey, I didn’t know this before I read/spoke to this person, so I’m noting where the idea or information came from originally.”  Without context, a reference functions the same as a citation. You refer to work done by other authors to support your own work. However, “References” can also refer to a specific page at the end of your work which lists all of the sources cited to support the work, IF you are working in APA style. In MLA and Chicago styles, this page would be called a Works Cited or Bibliography page, respectively.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is a bibliography the same thing as a citation?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'No. A citation can be either full or in-text and notates a source that the writer has used to support their argument or research. There are many styles of citations, but the most commonly used styles are the APA, MLA, and Chicago citation styles. A citation says, “Hey, I didn’t know this before I read/spoke to this person, so I’m noting where the idea or information came from originally.” A bibliography, on the other hand, is a complete list of sources used within a work to support your claims, ideas, and arguments. A Bibliography page, when specifically titled this way, is used in the Chicago style of citation formats, rather than calling the list of full citations a Works Cited (APA) or a References (MLA) page. A single citation could never be mistaken for bibliography, which by definition, is a list of citations.',
      },
    },
  ],
};

function CitationGenerator() {
  const classes = useStyles();
  const paperRef = useRef<HTMLDivElement | null>(null);
  const cgrContainerRef = useRef<HTMLDivElement | null>(null);
  const midContainerRef = useRef<HTMLDivElement | null>(null);
  const isMobile = useSelector((rootState: RootState) => rootState.common.platform.isMobile);
  const independentTool = useSelector((rootState: RootState) => rootState.common.independentTool);
  const isSmallScreen = useMediaQuery<Theme>((theme) => getCgBreakpointDown(theme));

  if (independentTool && isMobile) {
    return (
      <>
        <Head>
          <script
            type="application/ld+json"
            key="company-schema"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
          />
          <link
            href="https://fonts.googleapis.com/css2?family=Nunito+Sans:wght@400;600;700&display=swap"
            rel="stylesheet"
          />
        </Head>
        <Info />
        <Features />
        <HowCGWorks />
        <FormatsAndExample />
        <Basics />
        <CitationFaqSection />
      </>
    );
  }

  return (
    <>
      <Head>
        <script
          type="application/ld+json"
          key="company-schema"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Nunito+Sans:wght@400;600;700&display=swap"
          rel="stylesheet"
        />
      </Head>

      <StyledRootContainer
        maxWidth={false}
        className={`${classes.bgColorbody} root-container home-container-gray`}
        independentTool={independentTool}
      >
        <CitationGeneratorProvider>
          <EditFormProvider>
            <EditForm
              paperRef={paperRef}
              cgrContainerRef={cgrContainerRef}
              midContainerRef={midContainerRef}
            />

            <Grid container alignItems="center" justifyContent="center">
              <Grid
                item
                xs={12}
                className={`mid-container ${classes.midContainerResponsive}`}
                ref={midContainerRef}
              >
                <Paper
                  ref={paperRef}
                  elevation={independentTool || isSmallScreen ? 0 : 3}
                  square
                  style={{
                    margin: independentTool ? '' : '12px auto 0px',
                    position: 'relative',
                  }}
                >
                  {!(independentTool || isSmallScreen) && (
                    <Grid className={classes.feedbackBtnWrapper}>
                      <FeedbackButton />
                    </Grid>
                  )}

                  <Grid
                    container
                    id="cgr-container"
                    style={{
                      position: 'relative',
                    }}
                  >
                    <Grid
                      item
                      xs={12}
                      md={6}
                      className={`${classes.rightBorder} ${classes.pane}`}
                      {...(independentTool ? { style: { minHeight: 'calc(100vh - 23px)' } } : {})}
                      ref={cgrContainerRef}
                    >
                      <Grid container className={`${classes.topBar} ${classes.hideOnSmallScreens}`}>
                        <CitationResultHeader />
                      </Grid>

                      <ToolBarAndResultContainer />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <CitationListSection classes={classes} />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          </EditFormProvider>
        </CitationGeneratorProvider>
      </StyledRootContainer>

      {!independentTool && (
        <div id="education-area-wrapper">
          <Info />
          <Features />
          <HowCGWorks />
          <FormatsAndExample />
          <Basics />
          <CitationFaqSection />
        </div>
      )}
    </>
  );
}

export default withQuillLayout({
  children: CitationGenerator,
});

/* export async function getServerSideProps(context: DocumentContext) {
  const agent = detectAgent(context);

  if (agent.isMobile) {
    context.res?.writeHead(302, {
      Location: 'not-found',
    });
    context.res?.end();
  }

  return {
    props: {},
  };
} */
